import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppInfiniteQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { ProfileDemoApi } from "./profileDemo.api";

export namespace ProfileDemoQueries {

export const moduleName = QueryModule.ProfileDemo;

export const keys = {
    all: [moduleName] as const,
    getProfile: () => [...keys.all, "/profile-demo/profile", ] as const,
    getAnalyticsGrade: () => [...keys.all, "/profile-demo/analytics/grade", ] as const,
    getAnalyticsSkillDiagram: () => [...keys.all, "/profile-demo/analytics/skill-diagram", ] as const,
    getAnalyticsCompletion: () => [...keys.all, "/profile-demo/analytics/completion", ] as const,
    getAnalyticsActionPriorities: () => [...keys.all, "/profile-demo/analytics/action-priorities", ] as const,
    getAnalyticsStrengthWeaknesses: () => [...keys.all, "/profile-demo/analytics/strength-weaknesses", ] as const,
    getPast: (limit?: number, order?: string, page?: number, cursor?: string, ) => [...keys.all, "/profile-demo/live-shows/past", limit, order, page, cursor] as const,
    getPastInfinite: (limit?: number, order?: string, cursor?: string, ) => [...keys.all, "/profile-demo/live-shows/past", "infinite", limit, order, cursor] as const,
    getUpcoming: (limit?: number, order?: string, page?: number, cursor?: string, ) => [...keys.all, "/profile-demo/live-shows/upcoming", limit, order, page, cursor] as const,
    getUpcomingInfinite: (limit?: number, order?: string, cursor?: string, ) => [...keys.all, "/profile-demo/live-shows/upcoming", "infinite", limit, order, cursor] as const,
    getCurrent: () => [...keys.all, "/profile-demo/live-shows/current", ] as const,
    findById: (id: string, ) => [...keys.all, "/profile-demo/live-shows/:id", id] as const,
};


 /** 
 * Query `useGetProfile`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProfileDemoModels.DemoProfileResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetProfile = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getProfile, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getProfile(),
    queryFn: ProfileDemoApi.getProfile
    ,
    ...options,
  });
};






 /** 
 * Query `useGetAnalyticsGrade`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileAnalyticsGradeResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetAnalyticsGrade = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getAnalyticsGrade, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getAnalyticsGrade(),
    queryFn: ProfileDemoApi.getAnalyticsGrade
    ,
    ...options,
  });
};






 /** 
 * Query `useGetAnalyticsSkillDiagram`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileAnalyticsSkillDiagramResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetAnalyticsSkillDiagram = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getAnalyticsSkillDiagram, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getAnalyticsSkillDiagram(),
    queryFn: ProfileDemoApi.getAnalyticsSkillDiagram
    ,
    ...options,
  });
};






 /** 
 * Query `useGetAnalyticsCompletion`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileAnalyticsCompletionResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetAnalyticsCompletion = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getAnalyticsCompletion, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getAnalyticsCompletion(),
    queryFn: ProfileDemoApi.getAnalyticsCompletion
    ,
    ...options,
  });
};






 /** 
 * Query `useGetAnalyticsActionPriorities`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileAnalyticsActionPrioritiesV2ResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetAnalyticsActionPriorities = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getAnalyticsActionPriorities, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getAnalyticsActionPriorities(),
    queryFn: ProfileDemoApi.getAnalyticsActionPriorities
    ,
    ...options,
  });
};






 /** 
 * Query `useGetAnalyticsStrengthWeaknesses`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProfileAnalyticsStrengthsWeaknessesResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetAnalyticsStrengthWeaknesses = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getAnalyticsStrengthWeaknesses, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getAnalyticsStrengthWeaknesses(),
    queryFn: ProfileDemoApi.getAnalyticsStrengthWeaknesses
    ,
    ...options,
  });
};






 /** 
 * Query `useGetPast`
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProfileDemoModels.ProfileDemoGetPastResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetPast = <TData>({ limit, order, page, cursor }: { limit: number, order?: string, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof ProfileDemoApi.getPast, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getPast(limit, order, page, cursor),
    queryFn: () =>  
    
    ProfileDemoApi.getPast(limit, order, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetPastInfinite
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<ProfileDemoModels.ProfileDemoGetPastResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetPastInfinite = <TData>({ limit, order, cursor }: { limit: number, order?: string, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof ProfileDemoApi.getPast, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getPastInfinite(limit, order, cursor),
    queryFn: ({ pageParam }) =>  
    
    ProfileDemoApi.getPast(limit, order, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetUpcoming`
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProfileDemoModels.ProfileDemoGetUpcomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUpcoming = <TData>({ limit, order, page, cursor }: { limit: number, order?: string, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof ProfileDemoApi.getUpcoming, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getUpcoming(limit, order, page, cursor),
    queryFn: () =>  
    
    ProfileDemoApi.getUpcoming(limit, order, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetUpcomingInfinite
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<ProfileDemoModels.ProfileDemoGetUpcomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUpcomingInfinite = <TData>({ limit, order, cursor }: { limit: number, order?: string, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof ProfileDemoApi.getUpcoming, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getUpcomingInfinite(limit, order, cursor),
    queryFn: ({ pageParam }) =>  
    
    ProfileDemoApi.getUpcoming(limit, order, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetCurrent`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<ProfileDemoModels.ProfileDemoGetCurrentResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetCurrent = <TData>(options?: AppQueryOptions<typeof ProfileDemoApi.getCurrent, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getCurrent(),
    queryFn: ProfileDemoApi.getCurrent
    ,
    ...options,
  });
};






 /** 
 * Query `useFindById`
 * @param { string } object.id Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.LiveShowResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useFindById = <TData>({ id }: { id: string,  }, options?: AppQueryOptions<typeof ProfileDemoApi.findById, TData>) => {
  
  
  return useQuery({
    queryKey: keys.findById(id),
    queryFn: () =>  
    
    ProfileDemoApi.findById(id)
    ,
    ...options,
  });
};








 /** 
 * Mutation `useFinish`
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useFinish = (options?: AppMutationOptions<typeof ProfileDemoApi.finish, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            ProfileDemoApi.finish()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
